import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import EmailIcon from '@mui/icons-material/Email';
import LanguageIcon from '@mui/icons-material/Language';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { StorageAvatar } from '../components/common/StorageAvatar';
import { useAccount } from '../contexts/AccountContext';
import { useSnackbar } from '../contexts/SnackbarContext';
import {
  clearPartnerRelationship,
  downloadPartnerDocument,
  getPartner,
  getPartnerRelationship,
  updatePartnerRelationship,
} from '../services/partnerService';
import { PERMISSIONS } from '../shared/constants/permissions';
import type { Partner } from '../types/partner';

interface PartnerWithRelationship extends Partner {
  relationshipStatus?: 'approved' | 'rejected' | null;
}

const PartnerDetailsPage: React.FC = () => {
  const theme = useTheme();
  const { partnerId } = useParams<{ partnerId: string }>();
  const navigate = useNavigate();
  const { account, hasPermission } = useAccount();
  const { showSnackbar } = useSnackbar();

  const canManagePartner = hasPermission(PERMISSIONS.PARTNER_MANAGE);

  const [partner, setPartner] = useState<PartnerWithRelationship | null>(null);
  const [loading, setLoading] = useState(true);
  const [isUpdatingRelationship, setIsUpdatingRelationship] = useState(false);
  const [downloadingDocument, setDownloadingDocument] = useState(false);

  useEffect(() => {
    const fetchPartner = async () => {
      if (!partnerId || !account?.clientId) {
        return;
      }
      try {
        const partnerData = (await getPartner(partnerId)) as unknown as PartnerWithRelationship;

        if (partnerData) {
          // Fetch the relationship status for the current client
          const relData = await getPartnerRelationship(account.clientId, partnerId);

          if (relData) {
            partnerData.relationshipStatus = relData.status as 'approved' | 'rejected' | null;
          }

          setPartner(partnerData);
        } else {
          showSnackbar('Partner profile not found.', 'error');
        }
      } catch (_err) {
        showSnackbar('Failed to load partner profile.', 'error');
      } finally {
        setLoading(false);
      }
    };
    fetchPartner();
  }, [partnerId, account?.clientId, showSnackbar]);

  const handleDownloadCapabilityStatement = async () => {
    if (!partnerId || !partner?.capabilityStatementUrl) {
      return;
    }
    setDownloadingDocument(true);
    try {
      const blob = await downloadPartnerDocument(partnerId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Extract file extension from the gs:// URL
      let extension = 'docx';
      const parts = partner.capabilityStatementUrl.split('.');
      if (parts.length > 1) {
        const lastPart = parts.pop();
        if (lastPart && lastPart.length < 6 && !lastPart.includes('/')) {
          extension = lastPart;
        }
      }
      a.download = `Capability_Statement_${partner.companyName.replace(/[^a-zA-Z0-9]/g, '_')}.${extension}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err: unknown) {
      const error = err as Error;
      showSnackbar(`Failed to download document: ${error.message}`, 'error');
    } finally {
      setDownloadingDocument(false);
    }
  };

  const handleUpdateRelationship = async (status: 'approved' | 'rejected' | null) => {
    if (!account?.clientId || !partnerId) {
      return;
    }

    try {
      setIsUpdatingRelationship(true);
      if (status) {
        await updatePartnerRelationship(account.clientId, partnerId, status);
        showSnackbar(`Partner successfully marked as ${status}.`, 'success');
      } else {
        await clearPartnerRelationship(account.clientId, partnerId);
        showSnackbar('Partner relationship cleared.', 'info');
      }

      setPartner((prev) => (prev ? { ...prev, relationshipStatus: status } : null));
    } catch (err: unknown) {
      const e = err as Error;
      showSnackbar(`Failed to update relationship: ${e.message}`, 'error');
    } finally {
      setIsUpdatingRelationship(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!partner) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography variant="h5" color="error">
          Partner profile not found.
        </Typography>
        <Button
          variant="contained"
          size="large"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mt: 2, fontWeight: 'bold' }}
        >
          Go Back
        </Button>
      </Container>
    );
  }

  const handleContact = () => {
    if (!partner.contactEmail) {
      showSnackbar('Partner contact email is not available.', 'error');
      return;
    }
    const subject = encodeURIComponent(`Partnership Inquiry from BIDLOGIQ`);
    const body = encodeURIComponent(
      `Hi ${partner.companyName} team,\n\nWe are interested in discussing a potential partnership.\n\nPlease let us know when you might be available for a brief introductory call.\n\nThanks,`
    );
    window.location.href = `mailto:${partner.contactEmail}?subject=${subject}&body=${body}`;
  };

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Button
        variant="contained"
        size="large"
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 3, fontWeight: 'bold' }}
      >
        Back
      </Button>

      <Card variant="outlined" sx={{ position: 'relative' }}>
        {partner.relationshipStatus === 'approved' && (
          <Chip
            icon={<CheckCircleIcon />}
            label="Pre-approved"
            color="success"
            sx={{ position: 'absolute', top: 16, right: 16 }}
          />
        )}
        {partner.relationshipStatus === 'rejected' && (
          <Chip
            icon={<VisibilityOffIcon />}
            label="Pre-rejected"
            color="default"
            sx={{ position: 'absolute', top: 16, right: 16 }}
          />
        )}
        <CardHeader
          avatar={
            partner.logoUrl ? (
              <StorageAvatar
                src={partner.logoUrl}
                alt={partner.companyName}
                variant="square"
                imgProps={{ sx: { objectFit: 'contain' } }}
                sx={{ width: 80, height: 80, bgcolor: 'transparent' }}
              />
            ) : (
              <Avatar sx={{ width: 80, height: 80, bgcolor: 'primary.main', fontSize: '2rem' }}>
                {partner.companyName?.charAt(0)?.toUpperCase()}
              </Avatar>
            )
          }
          title={partner.companyName}
          subheader="Subcontractor / JV Partner Profile"
          titleTypographyProps={{ variant: 'h4', fontWeight: 'bold', sx: { pr: 12 } }}
        />
        <Divider />
        <CardContent sx={{ pt: 4, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {canManagePartner && (
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              {partner.relationshipStatus !== 'approved' && (
                <Button
                  variant="contained"
                  color="success"
                  size="large"
                  sx={{ fontWeight: 'bold' }}
                  onClick={() => handleUpdateRelationship('approved')}
                  disabled={isUpdatingRelationship}
                >
                  {isUpdatingRelationship ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Pre-approve Partner'
                  )}
                </Button>
              )}
              {partner.relationshipStatus !== 'rejected' && (
                <Button
                  variant="contained"
                  color="error"
                  size="large"
                  sx={{ fontWeight: 'bold' }}
                  onClick={() => handleUpdateRelationship('rejected')}
                  disabled={isUpdatingRelationship}
                >
                  {isUpdatingRelationship ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Pre-reject Partner'
                  )}
                </Button>
              )}
              {partner.relationshipStatus && (
                <Button
                  variant="contained"
                  color="inherit"
                  size="large"
                  sx={{ fontWeight: 'bold' }}
                  onClick={() => handleUpdateRelationship(null)}
                  disabled={isUpdatingRelationship}
                >
                  {isUpdatingRelationship ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Clear Status'
                  )}
                </Button>
              )}
            </Box>
          )}

          <Box>
            <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">
              Capability Statement Text
            </Typography>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {partner.capabilitiesSummary || 'No capability text provided.'}
            </Typography>
          </Box>

          {partner.additionalInfo && (
            <Box>
              <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">
                Additional Information
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {partner.additionalInfo}
              </Typography>
            </Box>
          )}

          <Box>
            <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">
              Contact Information
            </Typography>
            <Typography
              variant="body1"
              sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
            >
              <EmailIcon fontSize="small" color="action" /> <strong>Email:</strong>{' '}
              {partner.contactEmail || 'Not provided'}
            </Typography>
            {partner.contactPhone && (
              <Typography
                variant="body1"
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
              >
                <PhoneIcon fontSize="small" color="action" /> <strong>Phone:</strong>{' '}
                {partner.contactPhone}
              </Typography>
            )}
            {partner.website && (
              <Typography
                variant="body1"
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
              >
                <LanguageIcon fontSize="small" color="action" /> <strong>Website:</strong>{' '}
                <a href={partner.website} target="_blank" rel="noopener noreferrer">
                  {partner.website}
                </a>
              </Typography>
            )}
            {(partner.country || partner.region) && (
              <Typography
                variant="body1"
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
              >
                <LocationOnIcon fontSize="small" color="action" /> <strong>Location:</strong>{' '}
                {[partner.region, partner.country].filter(Boolean).join(', ')}
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              sx={{ fontWeight: 'bold' }}
              startIcon={<EmailIcon />}
              onClick={handleContact}
              disabled={!partner.contactEmail}
            >
              Contact Partner
            </Button>

            {partner.capabilityStatementUrl && (
              <Button
                variant="contained"
                size="large"
                sx={{
                  fontWeight: 'bold',
                  color: 'white',
                  backgroundColor: theme.palette.bidlogiq?.brick || 'primary.main',
                  '&:hover': {
                    backgroundColor: theme.palette.bidlogiq?.brick || 'primary.dark',
                    filter: 'brightness(0.9)',
                  },
                }}
                startIcon={
                  downloadingDocument ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <DownloadIcon />
                  )
                }
                onClick={handleDownloadCapabilityStatement}
                disabled={downloadingDocument}
              >
                {downloadingDocument ? 'Downloading...' : 'Download Capability Statement'}
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default PartnerDetailsPage;
